import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import styles from "./deferred.css";
import Modal from "../Modal";
import ContentLoadingOverlay from "./ContentLoadingOverlay";
import FocusLock, { InFocusGuard } from "react-focus-lock";
import { useEventListener } from "../useEventListener";
import { MODAL_TYPE } from "../constants";
import { IFRAME_MESSAGES } from "./constants";
import { usePublicApiManagement } from "./usePublicApiManagement";
import { getIFrameLoadFailureHandler } from "./getIFrameLoadFailureHandler";
import { useErrorLogging } from "./useErrorLogging";
import { validateMessage } from "./validateMessage";
import { validateOpenIFrameModalParams } from "./validateOpenIFrameModalParams";
import { withParams } from "@src/helpers/queryString";
import { toggleMainAppAriaHidden } from "@template/state/modules/layout";

const {
  RECEIVE_MODAL_READY,
  RECEIVE_MODAL_CLOSE_TRIGGERED,
  SEND_FOCUS_ON_CLOSE_BUTTON
} = IFRAME_MESSAGES;

const PublicIFrameModal = () => {
  const [open, setOpen] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [iFrameUrl, setIFrameUrl] = useState(null);
  const [focusOnCloseElement, setFocusOnCloseElement] = useState(null);
  const [iFrameTitle, setIFrameTitle] = useState(null);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState(MODAL_TYPE.SIDE_RIGHT);
  const [forceDOMRewrite, setForceDOMRewrite] = useState(true);

  const iFrameRef = useRef(null);

  const dispatch = useDispatch();

  const handleMessageFromIFrame = ({ data, origin, source }) => {
    if (
      !validateMessage({
        iframe: iFrameRef.current?.contentWindow,
        origin,
        source
      })
    ) {
      return;
    }

    if (data === RECEIVE_MODAL_CLOSE_TRIGGERED) {
      closeModal();
    }

    if (data === RECEIVE_MODAL_READY) {
      setContentReady(true);
    }

    if (data.event === RECEIVE_MODAL_READY) {
      if (!data.iFrameTitle) {
        setError(
          `${RECEIVE_MODAL_READY} event was received, but no iFrameTitle value was specified.`
        );
      } else {
        setIFrameTitle(data.iFrameTitle);
        setContentReady(true);
      }
    }
  };

  const closeModal = () => {
    setOpen(false);
    dispatch(toggleMainAppAriaHidden(false));
  };

  useEffect(() => {
    if (error) {
      setContentReady(false);
    }
  }, [error]);

  useEffect(() => {
    if (contentReady) {
      setError(null);
    }
  }, [contentReady]);

  useEffect(() => {
    if (open && contentReady && iFrameRef.current) {
      iFrameRef.current.contentWindow.focus();
      iFrameRef.current.contentWindow.postMessage(
        SEND_FOCUS_ON_CLOSE_BUTTON,
        window.location.origin
      );
    }
  }, [open, contentReady, iFrameRef]);

  useEventListener("message", handleMessageFromIFrame);
  useErrorLogging(error);

  const openIFrameModal = ({
    url,
    focusOnCloseElement,
    consumer,
    iFrameTitle,
    modalType,
    forceDOMRewrite = true
  }) => {
    const urlWithConsumer = withParams(url, { consumer });

    validateOpenIFrameModalParams({
      url: urlWithConsumer,
      focusOnCloseElement,
      consumer,
      iFrameTitle,
      modalType,
      forceDOMRewrite
    });

    if (forceDOMRewrite) {
      setContentReady(false);
      setError(null);
    }

    setIFrameTitle(iFrameTitle);
    setIFrameUrl(urlWithConsumer);
    setFocusOnCloseElement(focusOnCloseElement);
    setModalType(modalType);
    setForceDOMRewrite(forceDOMRewrite);

    setOpen(true);

    dispatch(toggleMainAppAriaHidden(true));
  };

  usePublicApiManagement(openIFrameModal);

  return (
    <FocusLock disabled={!open} noFocusGuards>
      <InFocusGuard>
        <Modal
          open={open}
          modalType={modalType}
          elementReceivingFocusOnClose={focusOnCloseElement}
          pageOverlayClickDelegate={closeModal}
          forceDOMRewrite={forceDOMRewrite || !!error}
        >
          <ContentLoadingOverlay
            showError={!!error}
            contentReady={contentReady}
            closeButtonClickDelegate={closeModal}
            moveFocusToCloseButton={open}
          />
          <iframe
            tabIndex="-1"
            className={styles.iFrame}
            data-testid="public-modal-iframe"
            frameBorder="0"
            seamless="seamless"
            title={iFrameTitle}
            sandbox="allow-scripts allow-same-origin"
            src={iFrameUrl}
            ref={iFrameRef}
            onLoad={getIFrameLoadFailureHandler(setError)}
          />
        </Modal>
      </InFocusGuard>
    </FocusLock>
  );
};

export default PublicIFrameModal;
